import type { ItemFeedback, ItemFeedbackEntry } from "@memorang/types";
import i18next from "i18next";

export const hasStaticFeedback = (feedback?: ItemFeedback): boolean =>
	Boolean(
		feedback?.length &&
			feedback.some((entry) => entry.type.toLowerCase() === "static"),
	);

export const findFeedbackByLanguage = (
	feedback: ItemFeedback | undefined,
): ItemFeedbackEntry | undefined => {
	if (!feedback || feedback.length === 0) {
		return undefined;
	}

	const targetLanguage = (i18next.language || "en-US").toLowerCase();

	let match = feedback.find(
		(entry) => entry.language.toLowerCase() === targetLanguage,
	);

	if (!match) {
		const languageCode = targetLanguage.split("-")[0];
		match = feedback.find((entry) =>
			entry.language.toLowerCase().startsWith(languageCode),
		);
	}

	return match || feedback[0];
};

type FeedbackReasonItem<TIcon extends string = string> = {
	title: string;
	description: string;
	icon: TIcon;
	value: string;
};

type TranslateFunction = (key: string) => string;

const getNegativeFeedbackReasons = <TIcon extends string = string>(
	t: TranslateFunction,
): FeedbackReasonItem<TIcon>[] => [
	{
		title: t("dialog.feedbackReasons.negative.inaccurate.title"),
		description: t("dialog.feedbackReasons.negative.inaccurate.description"),
		icon: "alert-circle-outline" as TIcon,
		value: "inaccurate",
	},
	{
		title: t("dialog.feedbackReasons.negative.notHelpful.title"),
		description: t("dialog.feedbackReasons.negative.notHelpful.description"),
		icon: "help-circle-outline" as TIcon,
		value: "not-helpful",
	},
	{
		title: t("dialog.feedbackReasons.negative.incomplete.title"),
		description: t("dialog.feedbackReasons.negative.incomplete.description"),
		icon: "content-cut" as TIcon,
		value: "incomplete",
	},
	{
		title: t("dialog.feedbackReasons.negative.hardToUnderstand.title"),
		description: t(
			"dialog.feedbackReasons.negative.hardToUnderstand.description",
		),
		icon: "head-question-outline" as TIcon,
		value: "hard-to-understand",
	},
	{
		title: t("dialog.feedbackReasons.negative.other.title"),
		description: t("dialog.feedbackReasons.negative.other.description"),
		icon: "pencil-outline" as TIcon,
		value: "other-reason",
	},
];

const getReportFeedbackReasons = <TIcon extends string = string>(
	t: TranslateFunction,
): FeedbackReasonItem<TIcon>[] => [
	{
		title: t("dialog.feedbackReasons.report.misinformation.title"),
		description: t("dialog.feedbackReasons.report.misinformation.description"),
		icon: "alert-octagram-outline" as TIcon,
		value: "misinformation",
	},
	{
		title: t("dialog.feedbackReasons.report.harmfulContent.title"),
		description: t("dialog.feedbackReasons.report.harmfulContent.description"),
		icon: "emoticon-angry-outline" as TIcon,
		value: "harmful-content",
	},
	{
		title: t("dialog.feedbackReasons.report.violatesPolicies.title"),
		description: t(
			"dialog.feedbackReasons.report.violatesPolicies.description",
		),
		icon: "email-remove-outline" as TIcon,
		value: "violates-policies",
	},
	{
		title: t("dialog.feedbackReasons.report.other.title"),
		description: t("dialog.feedbackReasons.report.other.description"),
		icon: "pencil-outline" as TIcon,
		value: "other-reason",
	},
];

export const getFeedbackReasonsByType = <TIcon extends string = string>(
	type: "negative" | "report" | undefined,
	t: TranslateFunction,
): FeedbackReasonItem<TIcon>[] | undefined => {
	if (type === "negative") {
		return getNegativeFeedbackReasons<TIcon>(t);
	}
	if (type === "report") {
		return getReportFeedbackReasons<TIcon>(t);
	}
	return undefined;
};
